import { useState, useEffect } from 'react'
import Background from './components/Background'
import DesktopIcons from './components/DesktopIcons'
import TerminalApp from './apps/TerminalApp'
import Widgets from './components/Widgets'
import Taskbar from './components/Taskbar'
import StartMenu from './components/StartMenu'
import WindowManager from './components/WindowManager'
import LicenseFooter from './components/LicenseFooter'
import FloatingCrumbIcon from './components/FloatingCrumbIcon'
import FloatingCloudWerxIcon from './components/FloatingCloudWerxIcon'
import ContextMenuProvider from './components/ContextMenuProvider'
import AltTabSwitcher from './components/AltTabSwitcher'
import MusicPlayer from './components/MusicPlayer'
import ShutdownEffect from './components/ShutdownEffect'
import { ThemeProvider } from './context/ThemeContext'
import { getDefaultGeometry, fitToViewport } from './lib/windowGeometry'

function App() {
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false)
  const [windows, setWindows] = useState([])
  const [nextZIndex, setNextZIndex] = useState(1000)
  
  // Alt+Tab state
  const [isAltTabOpen, setIsAltTabOpen] = useState(false)
  const [altTabSelectedIndex, setAltTabSelectedIndex] = useState(0)
  
  // Pinned apps state
  const [pinnedApps, setPinnedApps] = useState([
    'explorer', 'browser', 'terminal', 'store', 'notepad', 'spotify'
  ])
  
  // Shutdown easter egg state
  const [isShuttingDown, setIsShuttingDown] = useState(false)

  const toggleStartMenu = () => {
    setIsStartMenuOpen(!isStartMenuOpen)
  }

  const openApp = (appName) => {
    // Check if app is already open
    const existingWindow = windows.find(w => w.appName === appName)
    if (existingWindow) {
      // Focus the existing window
      focusWindow(existingWindow.id)
      return
    }

    // Size and place the window against the current screen. On a narrow screen
    // this fills the available area instead of cascading an 800x600 window off
    // the edge; per-app preferred sizes still apply where there is room.
    const { position, size } = getDefaultGeometry(appName, windows.length)

    const newWindow = {
      id: Date.now() + Math.random(), // Unique ID
      appName,
      position,
      size,
      zIndex: nextZIndex,
      isMinimized: false,
      isMaximized: false,
      isFocused: true
    }

    // Unfocus all other windows and add new one
    setWindows(prev => [
      ...prev.map(w => ({ ...w, isFocused: false })),
      newWindow
    ])
    setNextZIndex(prev => prev + 1)
    setIsStartMenuOpen(false) // Close start menu when opening app
  }

  const openChat = () => {
    openApp('crumb')
  }

  const closeWindow = (windowId) => {
    setWindows(prev => prev.filter(w => w.id !== windowId))
  }

  const focusWindow = (windowId) => {
    setWindows(prev => prev.map(w => ({
      ...w,
      isFocused: w.id === windowId,
      zIndex: w.id === windowId ? nextZIndex : w.zIndex
    })))
    if (windows.find(w => w.id === windowId)) {
      setNextZIndex(prev => prev + 1)
    }
  }

  const minimizeWindow = (windowId) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, isMinimized: !w.isMinimized } : w
    ))
  }

  const maximizeWindow = (windowId) => {
    setWindows(prev => prev.map(w => {
      if (w.id === windowId) {
        if (w.isMaximized) {
          // Restore from maximized state. The screen may have changed size or
          // orientation while the window was maximized, so refit on the way out
          // rather than restoring a geometry that no longer fits.
          const restored = w.previousSize
            ? { position: w.previousPosition, size: w.previousSize }
            : getDefaultGeometry(w.appName)

          return {
            ...w,
            isMaximized: false,
            ...fitToViewport({ ...w, ...restored, isMaximized: false })
          }
        } else {
          // Store current state before maximizing
          return {
            ...w,
            isMaximized: true,
            previousPosition: { ...w.position },
            previousSize: { ...w.size }
          }
        }
      }
      return w
    }))
  }

  const updateWindowPosition = (windowId, newPosition) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, position: newPosition } : w
    ))
  }

  const updateWindowSize = (windowId, newSize) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, size: newSize } : w
    ))
  }

  // Batch update function for better performance during resize operations
  const updateWindowBatch = (windowId, updates) => {
    setWindows(prev => prev.map(w => 
      w.id === windowId ? { ...w, ...updates } : w
    ))
  }

  const resetAllWindows = () => {
    setWindows(prev => prev.map((w, index) => ({
      ...w,
      ...getDefaultGeometry(w.appName, index),
      isMinimized: false,
      isMaximized: false
    })))
  }

  const snapWindow = (windowId, snapZone) => {
    if (!snapZone) return
    
    setWindows(prev => prev.map(w => {
      if (w.id === windowId) {
        // Store previous state for restore
        const updates = {
          position: { ...snapZone.position },
          size: { ...snapZone.size },
          isMaximized: snapZone.id === 'top', // Maximize for top snap
          previousPosition: w.previousPosition || { ...w.position },
          previousSize: w.previousSize || { ...w.size }
        }
        
        return {
          ...w,
          ...updates
        }
      }
      return w
    }))
    
    console.log(`Window snapped to ${snapZone.label}`)
  }

  // Handle personalize actions
  const handlePersonalize = (section) => {
    openApp('settings')
    // Optionally could switch to specific tab based on section
    console.log(`Opening settings for section: ${section || 'default'}`)
  }

  // Context menu action handlers
  const handleDesktopAction = (action, data) => {
    switch (action) {
      case 'refresh':
        console.log('Desktop refreshed')
        // Could trigger a re-render or reload of desktop icons
        break
      case 'personalize':
        handlePersonalize()
        break
      case 'display-settings':
        handlePersonalize()
        break
      case 'open-personalize':
        handlePersonalize(data?.section)
        break
      case 'properties':
        console.log('Desktop properties')
        // Could show system information dialog
        break
      default:
        console.log(`Desktop action: ${action}`)
    }
  }

  const handleWindowAction = (action, data) => {
    const windowId = data.window?.id
    if (!windowId) return

    switch (action) {
      case 'maximize':
        maximizeWindow(windowId)
        break
      case 'minimize':
        minimizeWindow(windowId)
        break
      case 'close':
        closeWindow(windowId)
        break
      case 'move':
      case 'size':
        console.log(`Window ${action} mode activated`)
        // These could activate special cursor modes
        break
      default:
        console.log(`Window action: ${action}`)
    }
  }

  const handleTaskbarAction = (action, data) => {
    switch (action) {
      case 'open':
        openApp(data.appName)
        break
      case 'close':
        const windowToClose = windows.find(w => w.appName === data.appName)
        if (windowToClose) {
          closeWindow(windowToClose.id)
        }
        break
      case 'pin':
        setPinnedApps(prev => {
          if (!prev.includes(data.appName)) {
            console.log(`📌 Pinned ${data.appName} to taskbar`)
            return [...prev, data.appName]
          }
          return prev
        })
        break
      case 'unpin':
        setPinnedApps(prev => {
          const newPinned = prev.filter(app => app !== data.appName)
          console.log(`📌 Unpinned ${data.appName} from taskbar`)
          return newPinned
        })
        break
      default:
        console.log(`Taskbar action: ${action}`)
    }
  }

  // Alt+Tab handlers
  const handleAltTabSelectionChange = (direction) => {
    if (windows.length === 0) return
    
    setAltTabSelectedIndex(prevIndex => {
      if (direction === 'next') {
        return (prevIndex + 1) % windows.length
      } else {
        return prevIndex === 0 ? windows.length - 1 : prevIndex - 1
      }
    })
  }

  const handleAltTabConfirm = () => {
    if (windows.length > 0 && altTabSelectedIndex < windows.length) {
      const selectedWindow = windows[altTabSelectedIndex]
      focusWindow(selectedWindow.id)
      
      // If window is minimized, restore it
      if (selectedWindow.isMinimized) {
        minimizeWindow(selectedWindow.id)
      }
    }
    setIsAltTabOpen(false)
    setAltTabSelectedIndex(0)
  }

  const handleAltTabCancel = () => {
    setIsAltTabOpen(false)
    setAltTabSelectedIndex(0)
  }

  // Keep windows on screen when the viewport changes. Rotating a phone swaps
  // width and height, which can leave a window sized for the old orientation
  // hanging off the edge with its title bar out of reach.
  useEffect(() => {
    let frame = null

    const refit = () => {
      // Orientation changes fire a burst of resize events, and iOS reports the
      // old dimensions on the first one. Settle before measuring.
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        setWindows(prev => {
          const next = prev.map(fitToViewport)
          // fitToViewport returns the same object when nothing moved, so this
          // skips the state update entirely on a no-op resize.
          return next.some((w, i) => w !== prev[i]) ? next : prev
        })
      })
    }

    globalThis.addEventListener('resize', refit)
    globalThis.addEventListener('orientationchange', refit)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      globalThis.removeEventListener('resize', refit)
      globalThis.removeEventListener('orientationchange', refit)
    }
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt+Tab functionality
      if (e.altKey && e.key === 'Tab' && !isAltTabOpen) {
        e.preventDefault()
        if (windows.length > 0) {
          setIsAltTabOpen(true)
          setAltTabSelectedIndex(0)
          console.log('Alt+Tab activated')
        }
        return
      }
      
      // Ctrl+R to reset windows to default positions
      if (e.ctrlKey && e.key === 'r') {
        e.preventDefault()
        resetAllWindows()
        console.log('Windows reset to default positions')
      }
      
      // Escape key to unmaximize focused window (only if Alt+Tab is not open)
      if (e.key === 'Escape' && !isAltTabOpen) {
        const focusedWindow = windows.find(w => w.isFocused && w.isMaximized)
        if (focusedWindow) {
          e.preventDefault()
          maximizeWindow(focusedWindow.id)
          console.log('Window unmaximized with Escape key')
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [windows, isAltTabOpen])

  // Shutdown easter egg handlers
  const handleShutdown = () => {
    console.log('🔌 Shutdown easter egg activated!')
    setIsShuttingDown(true)
  }

  const handleShutdownComplete = () => {
    setIsShuttingDown(false)
    console.log('✨ Welcome back to Pink Pixel OS!')
  }

  const handleDesktopRightClick = (event) => {
    // Check if right-clicking on desktop (not on icons, widgets, etc.)
    const target = event.target
    const isDesktopElement = target.classList.contains('bg-gray-900') || 
                             target.closest('.absolute.inset-0') || 
                             (event.currentTarget === event.target)
    
    // Only show context menu if clicking on desktop background
    if (isDesktopElement && !target.closest('.desktop-icon, .widget, .taskbar, .start-menu, .window')) {
      event.preventDefault()
      const contextMenuEvent = new CustomEvent('showContextMenu', {
        detail: { event, type: 'desktop', data: {} }
      })
      document.dispatchEvent(contextMenuEvent)
    }
  }

  return (
    <ThemeProvider>
    <ContextMenuProvider
      onDesktopAction={handleDesktopAction}
      onWindowAction={handleWindowAction}
      onTaskbarAction={handleTaskbarAction}
      onPersonalize={handlePersonalize}
      windows={windows}
      pinnedApps={pinnedApps}
    >
      <div 
        className="bg-gray-900 text-white h-screen overflow-hidden select-none relative"
        onContextMenu={handleDesktopRightClick}
      >
        {/* Futuristic background */}
        <Background />
        
        {/* Desktop icons */}
        <DesktopIcons onOpenApp={openApp} />
        
        {/* Widgets */}
        <Widgets onOpenApp={openApp} />
        
        {/* Taskbar */}
        <Taskbar 
          onToggleStartMenu={toggleStartMenu}
          onOpenApp={openApp}
          windows={windows}
          onFocusWindow={focusWindow}
          onMinimizeWindow={minimizeWindow}
          pinnedApps={pinnedApps}
        />
        
        {/* Start Menu */}
        {isStartMenuOpen && (
          <StartMenu 
            onOpenApp={openApp}
            onClose={() => setIsStartMenuOpen(false)}
            onShutdown={handleShutdown}
          />
        )}
        
        {/* Window Manager - handles all open windows */}
        <WindowManager 
          windows={windows}
          onClose={closeWindow}
          onFocus={focusWindow}
          onMinimize={minimizeWindow}
          onMaximize={maximizeWindow}
          onUpdatePosition={updateWindowPosition}
          onUpdateSize={updateWindowSize}
          onOpenApp={openApp}
          onSnapWindow={snapWindow}
        />
        
        {/* Floating Crumb Chat Icon */}
        <FloatingCrumbIcon onOpenChat={openChat} />
        
        {/* Floating CloudWerx Icon */}
        <FloatingCloudWerxIcon />
        
        {/* Music Player */}
        <MusicPlayer />
        
        {/* Alt+Tab Switcher */}
        <AltTabSwitcher
          isVisible={isAltTabOpen}
          windows={windows}
          selectedIndex={altTabSelectedIndex}
          onSelectionChange={handleAltTabSelectionChange}
          onConfirm={handleAltTabConfirm}
          onCancel={handleAltTabCancel}
        />
        
        {/* License Footer */}
        <LicenseFooter />
        
        {/* Shutdown Easter Egg Effect */}
        <ShutdownEffect 
          isShuttingDown={isShuttingDown}
          onComplete={handleShutdownComplete}
        />
      </div>
    </ContextMenuProvider>
    </ThemeProvider>
  )
}

export default App